import { Modal } from '@/components/common/dialog';
import { ModalTransportationButton } from '@/components/common/modal-transportation-button';
import { ElevatorIcon, EscalatorIcon, StairsIcon } from '@/components/icons';
import { useAccessibilityTTS } from '@/hooks';
import { TRANSPORTATION_REQUEST_TYPE } from 'dabeeomaps';
import { useTranslation } from 'react-i18next';

type Props = {
    routes?: TRANSPORTATION_REQUEST_TYPE[];
    isOpen?: boolean;
    onClose?: () => void;
    onSelect?: (transportationMode: TRANSPORTATION_REQUEST_TYPE) => void;
};

export const SelectTransportationModeModal = ({ isOpen = false, onClose = () => {}, onSelect = () => {}, routes }: Props) => {
    const { t } = useTranslation();
    const { speakSequentially } = useAccessibilityTTS();

    if (!isOpen || !Array.isArray(routes) || !routes.length) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="widescreen:max-w-[calc(680*var(--vw-unit))] max-w-[calc(618*var(--vw-unit))]">
            <div className="py-[calc(56*var(--vh-unit))]">
                <p
                    data-modal-region
                    data-interactive
                    tabIndex={0}
                    role="document"
                    aria-label={t('floor_guide.message.please_select_your_preferred_mode_of_transportation')}
                    className="text-text-main01 focus-border mx-[calc(10*var(--vw-unit))] text-center text-[calc(32*var(--vh-unit))] font-medium"
                >
                    {t('floor_guide.message.please_select_your_preferred_mode_of_transportation')}
                </p>
                <div className="mt-[calc(48*var(--vh-unit))] flex items-center justify-center gap-[calc(24*var(--vw-unit))]">
                    {routes.includes(TRANSPORTATION_REQUEST_TYPE.ESCALATOR) && (
                        <ModalTransportationButton
                            role="button"
                            tabIndex={0}
                            variant="tertiary"
                            icon={<EscalatorIcon className="text-text-white" />}
                            additionalClassName="flex flex-col gap-[calc(20*var(--vw-unit))]!"
                            onFocus={() => {
                                const text = `${t('floor_guide.button.escalator')} ${t('common.button.tts.button')}`;
                                speakSequentially(text);
                            }}
                            onMouseDown={(e) => {
                                if (e.detail !== 0) {
                                    e.preventDefault();
                                }
                            }}
                            onClick={() => {
                                onSelect(TRANSPORTATION_REQUEST_TYPE.ESCALATOR);
                            }}
                            data-tts-scope="transportation-mode-button"
                        >
                            {t('floor_guide.button.escalator')}
                        </ModalTransportationButton>
                    )}
                    {routes.includes(TRANSPORTATION_REQUEST_TYPE.ELEVATOR) && (
                        <ModalTransportationButton
                            role="button"
                            tabIndex={0}
                            variant="tertiary"
                            icon={<ElevatorIcon className="text-text-white" />}
                            additionalClassName="flex flex-col gap-[calc(20*var(--vw-unit))]!"
                            onFocus={() => {
                                const text = `${t('floor_guide.button.elevator')} ${t('common.button.tts.button')}`;
                                speakSequentially(text);
                            }}
                            onMouseDown={(e) => {
                                if (e.detail !== 0) {
                                    e.preventDefault();
                                }
                            }}
                            onClick={() => {
                                onSelect(TRANSPORTATION_REQUEST_TYPE.ELEVATOR);
                            }}
                            data-tts-scope="transportation-mode-button"
                        >
                            {t('floor_guide.button.elevator')}
                        </ModalTransportationButton>
                    )}
                    {routes.includes(TRANSPORTATION_REQUEST_TYPE.STAIRS) && (
                        <ModalTransportationButton
                            role="button"
                            tabIndex={0}
                            variant="tertiary"
                            icon={<StairsIcon className="text-text-white" />}
                            additionalClassName="flex flex-col gap-[calc(20*var(--vw-unit))]!"
                            onFocus={() => {
                                const text = `${t('floor_guide.button.stairs')} ${t('common.button.tts.button')}`;
                                speakSequentially(text);
                            }}
                            onMouseDown={(e) => {
                                if (e.detail !== 0) {
                                    e.preventDefault();
                                }
                            }}
                            onClick={() => {
                                onSelect(TRANSPORTATION_REQUEST_TYPE.STAIRS);
                            }}
                            data-tts-scope="transportation-mode-button"
                        >
                            {t('floor_guide.button.stairs')}
                        </ModalTransportationButton>
                    )}
                </div>
            </div>
        </Modal>
    );
};
